import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';

const fmt = (v, dec = 2) => v != null ? `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: dec, maximumFractionDigits: dec })}` : '—';
const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;

function Tile({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="text-[8px] text-gray-500 uppercase font-bold tracking-wide">{label}</div>
      <div className={`text-2xl font-extrabold mt-0.5 ${accent || 'text-navy'}`}>{value}</div>
      {sub && <div className="text-[8px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function ProgressBar({ value, max, color = 'bg-gold' }) {
  const pctVal = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pctVal}%` }} />
      </div>
      <span className="text-[8px] text-gray-500 w-8 text-right">{pctVal}%</span>
    </div>
  );
}

function WeekBar({ week }) {
  const submittedPct = pct(week.submitted + week.approved, week.total);
  const approvedPct  = pct(week.approved, week.total);
  const label = week.week_start?.slice(5) || '';
  return (
    <div className="flex items-center gap-3">
      <span className="text-[8px] text-gray-500 w-12 flex-shrink-0">{label}</span>
      <div className="flex-1 h-3 bg-gray-100 rounded overflow-hidden relative">
        <div className="h-full bg-green-400 rounded" style={{ width: `${submittedPct}%` }} />
        <div className="absolute inset-y-0 left-0 h-full bg-emerald-600 rounded" style={{ width: `${approvedPct}%` }} />
      </div>
      <span className="text-[8px] text-gray-500 w-20 flex-shrink-0">
        {week.approved}✓ {week.submitted}⏳ / {week.total}
      </span>
      <span className="text-[8px] text-gray-400 w-16 text-right flex-shrink-0">
        {week.total_hours ? `${Number(week.total_hours).toFixed(0)}h` : '0h'}
      </span>
    </div>
  );
}

export default function ResourcesDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('resource-dashboard/overview')
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[10px] text-gray-400 py-8 text-center">Loading dashboard…</div>;
  if (!data)   return <div className="text-[10px] text-red-500 py-8 text-center">Failed to load dashboard data.</div>;

  const { contractors, active_contracts, timesheets_this_week, invoices_this_month, invoices_all_time, recent_weeks, week_start } = data;
  const tsSubmitted = (timesheets_this_week?.submitted || 0) + (timesheets_this_week?.approved || 0);
  const tsExpected  = timesheets_this_week?.expected || 0;
  const tsMissing   = Math.max(0, tsExpected - tsSubmitted);

  return (
    <div className="fade-in space-y-4">
      <div>
        <h2 className="text-base font-extrabold text-navy">External Resources Dashboard</h2>
        <p className="text-[9px] text-gray-500 mt-0.5">Current week from {week_start || '—'}</p>
      </div>

      {/* Contractor overview */}
      <div>
        <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-2">Contractors</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile label="Total" value={contractors?.total ?? 0} sub={`${contractors?.active ?? 0} active`} />
          <Tile label="Active" value={contractors?.active ?? 0} accent="text-emerald-600"
                sub={`${contractors?.inactive ?? 0} inactive`} />
          <Tile label="Active Contracts" value={active_contracts?.total ?? 0}
                sub={active_contracts?.total_value ? `${fmt(active_contracts.total_value)} committed` : 'none'} />
          <Tile label="Contract Value" value={fmt(active_contracts?.total_value ?? 0)} accent="text-amber-600"
                sub="active contracts" />
        </div>
      </div>

      {/* Timesheets this week */}
      <div>
        <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-2">
          Timesheets — this week
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <Tile label="Expected" value={tsExpected} sub="active contractors" />
          <Tile label="Submitted" value={tsSubmitted} accent="text-blue-600"
                sub={`${timesheets_this_week?.approved ?? 0} approved`} />
          <Tile label="Missing" value={tsMissing}
                accent={tsMissing > 0 ? 'text-red-500' : 'text-emerald-600'}
                sub={tsMissing === 0 ? 'All up to date' : 'reminder sent Sun'} />
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-[8px] text-gray-500 uppercase font-bold tracking-wide mb-2">Coverage</div>
            <ProgressBar value={tsSubmitted} max={tsExpected}
              color={tsSubmitted >= tsExpected ? 'bg-emerald-500' : 'bg-amber-400'} />
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div>
        <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-2">Invoices</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile label="This month" value={fmt(invoices_this_month?.total ?? 0)}
                sub={`${invoices_this_month?.count ?? 0} invoice${invoices_this_month?.count !== 1 ? 's' : ''}`} />
          <Tile label="Pending approval" value={fmt(invoices_this_month?.pending ?? 0)} accent="text-amber-600" />
          <Tile label="Paid this month"  value={fmt(invoices_this_month?.paid ?? 0)} accent="text-emerald-600" />
          <Tile label="All time paid"    value={fmt(invoices_all_time?.paid ?? 0)} accent="text-navy"
                sub={`of ${fmt(invoices_all_time?.total ?? 0)} total`} />
        </div>
      </div>

      {/* Timesheet history */}
      {recent_weeks && recent_weeks.length > 0 && (
        <div>
          <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-2">
            Recent weeks
            <span className="ml-2 normal-case font-normal text-gray-300">dark green = approved, light green = submitted</span>
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
            {recent_weeks.map(w => <WeekBar key={w.week_start} week={w} />)}
          </div>
        </div>
      )}
    </div>
  );
}
